import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Avatar,
  CircularProgress,
  Alert,
  Chip,
  Pagination,
  Stack,
} from "@mui/material";
import {
  FormatQuote,
  Star,
  Storefront,
  Person,
} from "@mui/icons-material";
import { alpha, useTheme } from "@mui/material/styles";
import { useTranslation } from "react-i18next";
import { sellerReviewService, SellerReview } from "../services/sellerReviews";
import PageTitle from "./PageTitle";
import ReviewCommentBody from "./ReviewCommentBody";
import { ReviewCriteriaGrid } from "./ReviewCriteriaRatings";

const PublicSellerReviews: React.FC = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const [reviews, setReviews] = useState<SellerReview[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [expandedCommentIds, setExpandedCommentIds] = useState<Set<string>>(new Set());
  const limit = 3;

  const setCommentExpanded = (reviewId: string, expanded: boolean) => {
    setExpandedCommentIds((prev) => {
      const next = new Set(prev);
      if (expanded) {
        next.add(reviewId);
      } else {
        next.delete(reviewId);
      }
      return next;
    });
  };

  useEffect(() => {
    const fetchReviews = async () => {
      setLoading(true);
      setError(null);
      try {
        const { data, count, error: fetchError } = await sellerReviewService.getAllReviews(page, limit);
        if (fetchError) throw fetchError;
        setReviews(data || []);
        setTotalPages(Math.ceil((count || 0) / limit) || 1);
        setTotalCount(count || 0);
      } catch (err: any) {
        console.error("Error fetching reviews:", err);
        setError(t("reviews.fetchError", { defaultValue: "Failed to load reviews" }));
      } finally {
        setLoading(false);
      }
    };

    fetchReviews();
  }, [page, t]);

  if (loading) {
    return (
      <Box id="reviews" sx={{ display: "flex", justifyContent: "center", py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box id="reviews" sx={{ mt: 6, mb: 8 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  if (reviews.length === 0) {
    return (
      <Box id="reviews" sx={{ mt: 6, mb: 8, display: 'flex', justifyContent: 'center' }}>
        <Typography color="text.secondary">
          {t("reviews.noReviews", { defaultValue: "No reviews yet." })}
        </Typography>
      </Box>
    );
  }

  return (
    <Box id="reviews" sx={{ mt: 6, mb: 8 }}>
      <PageTitle
        text={t("reviews.sellerRatings", { defaultValue: "Seller Performance Ratings" })}
        align="center"
        size="medium"
      />

      <Box sx={{ display: "flex", justifyContent: "center", mt: -1, mb: 4 }}>
        <Chip
          label={`${totalCount} ${t("reviews.count")}`}
          variant="outlined"
          size="small"
        />
      </Box>

      <Grid container spacing={3}>
        {reviews.map((review) => {
          const sellerName =
            review.seller?.company_name ||
            review.seller?.full_name ||
            t("reviews.unknownSeller", { defaultValue: "Unknown Seller" });

          const reviewerName =
            review.reviewer?.full_name ||
            t("reviews.unknownReviewer", { defaultValue: "Unknown Reviewer" });

          const isCommentExpanded = expandedCommentIds.has(review.id);

          return (
            <Grid key={review.id} size={{ xs: 12, md: 6, lg: 4 }} sx={{ display: "flex", minWidth: 0 }}>
              <Card
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  width: "100%",
                  minHeight: 100,
                  borderRadius: 4,
                  border: "1px solid",
                  borderColor: alpha(theme.palette.primary.main, 0.14),
                  boxShadow: `0 14px 34px ${alpha(theme.palette.common.black, isDark ? 0.24 : 0.08)}`,
                  background: `linear-gradient(180deg, ${alpha(theme.palette.primary.light, isDark ? 0.14 : 0.08)} 0%, ${theme.palette.background.paper} 28%)`,
                  transition: "transform 0.25s ease, box-shadow 0.25s ease, border-color 0.25s ease",
                  overflow: isCommentExpanded ? "visible" : "hidden",
                  "&:hover": {
                    transform: "translateY(-4px)",
                    boxShadow: `0 20px 44px ${alpha(theme.palette.common.black, isDark ? 0.3 : 0.14)}`,
                    borderColor: alpha(theme.palette.primary.main, 0.28),
                  },
                }}
              >
                <CardContent sx={{ p: { xs: 2, sm: 3 }, display: "flex", flex: 1 }}>
                  <Stack spacing={2.5} sx={{ width: "100%" }}>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: { xs: "stretch", sm: "flex-start" },
                        justifyContent: "space-between",
                        gap: 2,
                        flexDirection: { xs: "column", sm: "row" },
                      }}
                    >
                      <Stack direction="row" spacing={1.5} alignItems="center" sx={{ minWidth: 0 }}>
                        <Avatar
                          sx={{
                            width: 52,
                            height: 52,
                            bgcolor: alpha(theme.palette.primary.main, 0.12),
                            color: "primary.main",
                            fontWeight: 700,
                          }}
                        >
                          <Storefront />
                        </Avatar>

                        <Box sx={{ minWidth: 0 }}>
                          <Typography
                            variant="h6"
                            sx={{
                              fontWeight: 800,
                              color: "text.primary",
                              lineHeight: 1.2,
                              mb: 0.5,
                              fontSize: { xs: "1rem", sm: "1.25rem" },
                              wordBreak: "break-word",
                              overflowWrap: "anywhere",
                            }}
                          >
                            {sellerName}
                          </Typography>

                          <Stack
                            direction="column"
                            spacing={0.5}
                          >
                            <Typography variant="body2" color="text.secondary">
                              {t("reviews.reviewedBy", { defaultValue: "Reviewed by" })}: {reviewerName}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {new Date(review.created_at).toLocaleDateString()}
                            </Typography>
                          </Stack>
                        </Box>
                      </Stack>

                      <Chip
                        icon={<Star sx={{ color: "#fff !important" }} />}
                        label={`${review.overall_rating.toFixed(1)} / 5`}
                        sx={{
                          flexShrink: 0,
                          height: 36,
                          borderRadius: 999,
                          px: 1,
                          fontWeight: 800,
                          color: theme.palette.common.white,
                          background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
                          boxShadow: `0 10px 24px ${alpha(theme.palette.primary.main, 0.28)}`,
                          alignSelf: { xs: "stretch", sm: "auto" },
                          width: { xs: "100%", sm: "auto" },
                          justifyContent: "center",
                        }}
                      />
                    </Box>

                    <ReviewCriteriaGrid review={review} />

                    {review.comment && (
                      <Box
                        sx={{
                          position: "relative",
                          p: { xs: 1.25, sm: 1.5 },
                          pl: { xs: 1.5, sm: 1.75 },
                          borderRadius: 3,
                          bgcolor: alpha(theme.palette.info.main, 0.05),
                          border: "1px solid",
                          borderColor: alpha(theme.palette.info.main, 0.12),
                          display: "flex",
                          flexDirection: "column",
                        }}
                      >
                        <FormatQuote
                          sx={{
                            position: "absolute",
                            top: 8,
                            right: 8,
                            fontSize: 22,
                            color: alpha(theme.palette.info.main, 0.18),
                          }}
                        />
                        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5, flexShrink: 0 }}>
                          <Person sx={{ fontSize: 16, color: "info.main" }} />
                          <Typography variant="subtitle2" sx={{ fontWeight: 700, fontSize: "0.78rem" }}>
                            {t("reviews.customerFeedback", { defaultValue: "Customer feedback" })}
                          </Typography>
                        </Stack>
                        <ReviewCommentBody
                          comment={review.comment}
                          maxLines={4}
                          onExpandedChange={(expanded) => setCommentExpanded(review.id, expanded)}
                          typographyProps={{
                            sx: {
                              fontSize: { xs: "0.82rem", sm: "0.78rem" },
                              lineHeight: 1.5,
                              pr: 2.25,
                            },
                          }}
                        />
                      </Box>
                    )}
                  </Stack>
                </CardContent>
              </Card>
            </Grid>
          );
        })}
      </Grid>

      {totalPages > 1 && (
        <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
          <Pagination
            count={totalPages}
            page={page}
            onChange={(_, value) => setPage(value)}
            color="primary"
          />
        </Box>
      )}
    </Box>
  );
};

export default PublicSellerReviews;